import { Navigate } from 'react-router-dom'

import {
  CompetenciesProfileRootPageDocument,
  CompetenciesProfileRootPageQuery,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { usePagination } from 'modules/competencies/hooks/usePagination'
import CollectionsBootstrap from 'modules/competencies/lib/ProfileCollectionsBootstrap'
import Competencies from 'modules/competencies/pages/employees/Competencies'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface ContainerProps {
  competenciesAvailable: boolean
  data?: CompetenciesProfileRootPageQuery
  hasNextPage: boolean
  hasPreviousPage: boolean
  isEmpty: boolean
  onNextPage: () => void
  onPreviousPage: () => void
}

const Container = ({
  competenciesAvailable,
  data,
  hasNextPage,
  hasPreviousPage,
  isEmpty,
  onNextPage,
  onPreviousPage,
}: ContainerProps) => {
  const { navigation } = useNavigation()

  const canManageCompetencies = data?.competencies._policies.competenciesManage.can ?? false

  if (!competenciesAvailable && canManageCompetencies)
    return <Navigate replace to={navigation.discover.marketplaceModules.fullPath()} />

  if (!competenciesAvailable) return <NotFound />

  return (
    <Competencies
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      assessmentGroup={data?.competencies.assessmentGroupsConnection.edges[0]?.node}
      isEmpty={isEmpty}
    />
  )
}

const RootPage = () => {
  const {
    employee: { id: employeeId },
    company: { id: companyId },
  } = useCurrentSession()
  const { pageNumber, handleNextPage, handlePreviousPage } = usePagination()

  const { loading, data, error } = useQuery(CompetenciesProfileRootPageDocument, {
    variables: {
      companyId: companyId,
      featureIds: ['competencies_core'],
      offsetPage: pageNumber,
      targetEmployeeIds: [employeeId.toString()],
    },
  })

  const pageInfo = data?.competencies.assessmentGroupsConnection.pageInfo
  const totalCount = data?.competencies.assessmentGroupsConnection.totalCount ?? 0
  const isEmpty = totalCount === 0
  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes ?? []
  const competenciesAvailable =
    featureAvailabilities.find((feature) => feature.id === 'competencies_core')?.available ?? false

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="full">
      <CollectionsBootstrap>
        <Container
          competenciesAvailable={competenciesAvailable}
          data={data}
          hasNextPage={pageInfo?.hasNextPage ?? false}
          hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
          isEmpty={isEmpty}
          onNextPage={() => handleNextPage(totalCount)}
          onPreviousPage={handlePreviousPage}
        />
      </CollectionsBootstrap>
    </ResponsivePage.Body>
  )
}

export default RootPage
